"use client";

import React, { memo, useCallback } from 'react';
import styles from './FilterGroup.module.scss';
import { FilterGroupProps } from './FilterGroup.types';
import Text from '@components/atoms/Text';
import { filterHelpers } from '@helpers/filterLogic';
import Image from 'next/image';
import Button from '@components/atoms/Button';
import assetLoader from '@utils/internalImageLoder';
import SharedFilterOption from '@components/atoms/SharedFilterOption';

const FilterGroup = memo<FilterGroupProps>(({
  title,
  options,
  selectedValues,
  onSelect,
  isOpen = false,
  onToggle,
  allowMultiSelect = true,
  category,
  filterOptions
}) => {

  const handleOptionSelect = useCallback((value: string) => {
    if (category && filterOptions) {
      const newValues = filterHelpers.handleHierarchicalFilterSelection(
        selectedValues,
        value,
        filterOptions,
        category
      );
      onSelect(newValues);
    } else {
      if (allowMultiSelect) {
        const newValues = selectedValues.includes(value)
          ? selectedValues.filter((v: string) => v !== value)
          : [...selectedValues, value];
        onSelect(newValues);
      } else {
        const newValues = selectedValues.includes(value) ? [] : [value];
        onSelect(newValues);
      }
    }
  }, [allowMultiSelect, selectedValues, onSelect, category, filterOptions]);

  return (
    <div className={styles.filterGroup}>
      <Button
        className={styles.filterHeader}
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-label={`Toggle ${title} filter section`}
      >
        <Text className={styles.filterTitle}>
          {title} {selectedValues.length > 0 && `(${selectedValues.length})`}
        </Text>
        {onToggle &&
          (isOpen ? (
            <Image
              src={`${assetLoader()}/icons/Chevron_up_dark.svg`}
              alt="Chevron_up_dark"
              width={20}
              height={20}
              loading="lazy"
            />
          ) : (
            <Image
              src={`${assetLoader()}/icons/Chevron_down_dark.svg`}
              alt="Chevron_down_dark"
              width={20}
              height={20}
              loading="lazy"
            />
          ))}
      </Button>
      {isOpen && (
        <div className={styles.optionsContainer}>
          {options.map((option, index) => (
            <SharedFilterOption
              key={`${category}-${option.value}-${index}`}
              option={{
                label: option.label ?? "",
                value: option.value,
                subcategories: option.subcategories || []
              }}
              selectedValues={selectedValues}
              onSelect={handleOptionSelect}
              showSubcategories={true}
            />
          ))}
        </div>
      )}
    </div>
  );
});

FilterGroup.displayName = "FilterGroup";

export default FilterGroup;
